import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, LogOut, Plus, Pencil, Trash2, Eye, EyeOff, Check, Undo2, Languages, Lightbulb } from "lucide-react";
import { api, formatApiErrorDetail, formatDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ARTICLE_CATEGORIES } from "../config/site";

const EMPTY_ARTICLE = {
  title: "", slug: "", excerpt: "", category: ARTICLE_CATEGORIES[0],
  cover_image: "", content: "", published: false,
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("artigos");
  const [articles, setArticles] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [editing, setEditing] = useState(null); // null | {id?, ...fields}
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [ideas, setIdeas] = useState([]);
  const [ideasLoading, setIdeasLoading] = useState(false);

  const load = () => {
    api.get("/admin/articles").then((r) => setArticles(r.data)).catch(() => {});
    api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => {});
  };

  useEffect(load, []);

  const doLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing.id) {
        await api.put(`/admin/articles/${editing.id}`, editing);
        toast.success("Artigo atualizado.");
      } else {
        await api.post("/admin/articles", editing);
        toast.success("Artigo criado.");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error("Erro ao guardar.", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/articles/${id}`);
      toast.success("Artigo eliminado.");
      load();
    } catch (err) {
      toast.error("Erro ao eliminar.");
    }
  };

  const toggleBooking = async (b) => {
    const next = b.status === "contactado" ? "pendente" : "contactado";
    try {
      await api.patch(`/admin/bookings/${b.id}`, { status: next });
      toast.success(next === "contactado" ? "Marcado como contactado." : "Pedido reaberto.");
      load();
    } catch (err) {
      toast.error("Erro ao atualizar o pedido.");
    }
  };

  const translateEN = async () => {
    if (!editing.title.trim() && !editing.content.trim()) {
      toast.error("Escreva primeiro o título e o conteúdo em português.");
      return;
    }
    setTranslating(true);
    try {
      const { data } = await api.post("/admin/translate", {
        title: editing.title,
        excerpt: editing.excerpt,
        content: editing.content,
      });
      setEditing((f) => ({ ...f, title_en: data.title_en, excerpt_en: data.excerpt_en, content_en: data.content_en }));
      toast.success("Tradução concluída.", { description: "Reveja a versão inglesa antes de guardar." });
    } catch (err) {
      toast.error("A tradução falhou.", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setTranslating(false);
    }
  };

  const suggestIdeas = async () => {
    setIdeasLoading(true);
    try {
      const { data } = await api.post("/admin/article-ideas");
      setIdeas(data.ideas || []);
    } catch (err) {
      toast.error("A geração de temas falhou.", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setIdeasLoading(false);
    }
  };

  const applyIdea = (idea) => {
    setIdeas([]);
    setEditing({ ...EMPTY_ARTICLE, title: idea.titulo });
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.next !== pw.confirm) {
      toast.error("As palavras-passe não coincidem.");
      return;
    }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Palavra-passe alterada com sucesso.");
      setPw({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error("Não foi possível alterar a palavra-passe.", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setPwSaving(false);
    }
  };

  const set = (k) => (e) =>
    setEditing((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-alabaster pt-10 pb-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <header className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <div>
            <p className="overline-label mb-2">Área Reservada</p>
            <h1 className="font-serif text-3xl font-semibold text-ebony">Gestão de conteúdos</h1>
            <p className="text-xs text-[#757A70] mt-2">{user?.email}</p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-back-site" className="btn-outline !py-2.5 !px-5">
              <ArrowLeft size={13} /> Ver site
            </Link>
            <button onClick={doLogout} data-testid="admin-logout-button" className="btn-outline !py-2.5 !px-5">
              <LogOut size={13} /> Sair
            </button>
          </div>
        </header>

        <div className="flex gap-2 mb-10 border-b border-[#E2E4DC]">
          {[["artigos", `Artigos (${articles.length})`], ["marcacoes", `Marcações (${bookings.length})`], ["seguranca", "Segurança"]].map(([key, label]) => (
            <button
              key={key}
              data-testid={`admin-tab-${key}`}
              onClick={() => setTab(key)}
              className={`font-mono text-[11px] uppercase tracking-[0.2em] px-5 py-3 border-b-2 -mb-px transition-colors ${
                tab === key ? "border-forest text-forest" : "border-transparent text-[#757A70] hover:text-ebony"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "artigos" && (
          <>
            {editing ? (
              <form onSubmit={save} data-testid="admin-article-form" className="bg-white border border-[#E2E4DC] p-8 sm:p-10 mb-12">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold mb-8">
                  {editing.id ? "Editar Artigo" : "Novo Artigo"}
                </p>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="sm:col-span-2">
                    <label className="overline-label block mb-2 !text-[10px]">Título *</label>
                    <input data-testid="admin-article-title-input" required value={editing.title} onChange={set("title")} className="input-field" />
                  </div>
                  <div>
                    <label className="overline-label block mb-2 !text-[10px]">Slug (opcional)</label>
                    <input data-testid="admin-article-slug-input" value={editing.slug} onChange={set("slug")} className="input-field" placeholder="gerado automaticamente" />
                  </div>
                  <div>
                    <label className="overline-label block mb-2 !text-[10px]">Categoria</label>
                    <select data-testid="admin-article-category-select" value={editing.category} onChange={set("category")} className="input-field">
                      {ARTICLE_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="overline-label block mb-2 !text-[10px]">Imagem de capa (URL)</label>
                    <input data-testid="admin-article-cover-input" value={editing.cover_image} onChange={set("cover_image")} className="input-field" placeholder="https://images.unsplash.com/…" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="overline-label block mb-2 !text-[10px]">Resumo</label>
                    <textarea data-testid="admin-article-excerpt-input" rows={2} value={editing.excerpt} onChange={set("excerpt")} className="input-field resize-none" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="overline-label block mb-2 !text-[10px]">Conteúdo</label>
                    <textarea data-testid="admin-article-content-input" rows={14} value={editing.content} onChange={set("content")} className="input-field resize-y font-mono !text-[13px]" />
                    <p className="text-[11px] text-[#9AA096] mt-2 leading-relaxed">
                      Formatação: **negrito**, *itálico*, ## Título, &gt; citação em destaque,
                      [^1] referência no texto e [^1]: nota de rodapé no fim do artigo.
                    </p>
                  </div>
                  <div className="sm:col-span-2 border-t border-[#E2E4DC] pt-8 mt-2 flex flex-wrap items-start justify-between gap-4">
                    <div className="max-w-sm">
                      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold mb-2">Versão Inglesa (opcional)</p>
                      <p className="text-[11px] text-[#9AA096] leading-relaxed">
                        Se preenchida, os visitantes em inglês veem esta versão do artigo. Se ficar vazia, veem a versão portuguesa.
                      </p>
                    </div>
                    <button
                      type="button"
                      data-testid="admin-translate-en-button"
                      onClick={translateEN}
                      disabled={translating}
                      className="btn-outline !py-2.5 !px-5 disabled:opacity-60"
                    >
                      <Languages size={13} /> {translating ? "A traduzir…" : "Traduzir para Inglês"}
                    </button>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="overline-label block mb-2 !text-[10px]">Título (EN)</label>
                    <input data-testid="admin-article-title-en-input" value={editing.title_en || ""} onChange={set("title_en")} className="input-field" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="overline-label block mb-2 !text-[10px]">Resumo (EN)</label>
                    <textarea data-testid="admin-article-excerpt-en-input" rows={2} value={editing.excerpt_en || ""} onChange={set("excerpt_en")} className="input-field resize-none" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="overline-label block mb-2 !text-[10px]">Conteúdo (EN)</label>
                    <textarea data-testid="admin-article-content-en-input" rows={10} value={editing.content_en || ""} onChange={set("content_en")} className="input-field resize-y font-mono !text-[13px]" />
                  </div>
                  <label className="sm:col-span-2 inline-flex items-center gap-3 cursor-pointer">
                    <input data-testid="admin-article-published-checkbox" type="checkbox" checked={editing.published} onChange={set("published")} className="w-4 h-4 accent-[#2D4030]" />
                    <span className="text-sm text-[#4D5248]">Publicado (visível no blog)</span>
                  </label>
                </div>
                <div className="flex gap-3 mt-10">
                  <button type="submit" data-testid="admin-article-save-button" disabled={saving} className="btn-primary disabled:opacity-60">
                    {saving ? "A guardar…" : "Guardar"}
                  </button>
                  <button type="button" data-testid="admin-article-cancel-button" onClick={() => setEditing(null)} className="btn-outline">
                    Cancelar
                  </button>
                </div>
              </form>
            ) : (
              <>
              <div className="mb-10 flex flex-wrap items-center gap-3">
                <button
                  data-testid="admin-create-article-button"
                  onClick={() => setEditing({ ...EMPTY_ARTICLE })}
                  className="btn-primary"
                >
                  <Plus size={14} /> Novo Artigo
                </button>
                <button
                  data-testid="admin-ideas-button"
                  onClick={suggestIdeas}
                  disabled={ideasLoading}
                  className="btn-outline disabled:opacity-60"
                >
                  <Lightbulb size={14} /> {ideasLoading ? "A gerar temas…" : "Sugerir Temas"}
                </button>
              </div>

              {ideas.length > 0 && (
                <div className="bg-white border border-[#E2E4DC] mb-10" data-testid="admin-ideas-list">
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold px-6 pt-5 pb-4 border-b border-[#E2E4DC]">
                    Temas sugeridos pelo Claude
                  </p>
                  {ideas.map((idea, i) => (
                    <div key={i} data-testid={`admin-idea-${i}`} className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-[#E2E4DC] last:border-b-0">
                      <div className="min-w-0">
                        <p className="font-serif text-base text-ebony">{idea.titulo}</p>
                        <p className="text-xs text-[#757A70] mt-0.5">{idea.angulo}</p>
                      </div>
                      <button
                        data-testid={`admin-idea-use-${i}`}
                        onClick={() => applyIdea(idea)}
                        className="btn-outline !py-1.5 !px-4 shrink-0"
                      >
                        Usar este tema
                      </button>
                    </div>
                  ))}
                </div>
              )}
              </>
            )}

            <div className="space-y-px bg-[#E2E4DC] border border-[#E2E4DC]" data-testid="admin-articles-list">
              {articles.length === 0 && (
                <p className="bg-white p-8 text-sm text-[#757A70]">Ainda não existem artigos.</p>
              )}
              {articles.map((a, i) => (
                <div key={a.id} data-testid={`admin-article-row-${i}`} className="bg-white p-5 flex flex-wrap items-center gap-4 justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      {a.published ? (
                        <span className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-forest"><Eye size={11} /> Publicado</span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-[#A88B4A]"><EyeOff size={11} /> Rascunho</span>
                      )}
                      <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#9AA096]">{a.category}</span>
                    </div>
                    <p className="font-serif text-lg text-ebony truncate">{a.title}</p>
                    <p className="text-[11px] text-[#9AA096] mt-0.5">{formatDate(a.created_at)} · /blog/{a.slug}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      data-testid={`admin-edit-article-${i}`}
                      onClick={() => setEditing({ ...a })}
                      className="btn-outline !py-2 !px-4"
                    >
                      <Pencil size={12} /> Editar
                    </button>
                    <button
                      data-testid={`admin-delete-article-${i}`}
                      onClick={() => remove(a.id)}
                      className="btn-outline !py-2 !px-4 !border-[#A33B2E]/40 !text-[#A33B2E] hover:!border-[#A33B2E]"
                    >
                      <Trash2 size={12} /> Eliminar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === "marcacoes" && (
          <div className="space-y-px bg-[#E2E4DC] border border-[#E2E4DC]" data-testid="admin-bookings-list">
            {bookings.length === 0 && (
              <p className="bg-white p-8 text-sm text-[#757A70]">Ainda não existem pedidos de marcação.</p>
            )}
            {bookings.map((b, i) => (
              <div key={b.id} data-testid={`admin-booking-row-${i}`} className="bg-white p-6">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                  <p className="font-serif text-lg text-ebony">{b.nome}</p>
                  <div className="flex items-center gap-3">
                    <span className={`font-mono text-[9px] uppercase tracking-[0.2em] px-3 py-1 border ${
                      b.status === "contactado"
                        ? "text-forest border-forest/40 bg-forest/5"
                        : "text-[#A88B4A] border-[#A88B4A]/30"
                    }`}>
                      {b.status}
                    </span>
                    <button
                      data-testid={`admin-booking-toggle-${i}`}
                      onClick={() => toggleBooking(b)}
                      className="btn-outline !py-1.5 !px-3"
                    >
                      {b.status === "contactado" ? <><Undo2 size={11} /> Reabrir</> : <><Check size={11} /> Marcar como contactado</>}
                    </button>
                  </div>
                </div>
                {b.resumo && (
                  <p data-testid={`admin-booking-summary-${i}`} className="text-sm font-serif italic text-forest leading-relaxed mb-3 border-l-2 border-gold/50 pl-3">
                    {b.resumo}
                  </p>
                )}
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1.5 text-sm text-[#4D5248]">
                  <p><span className="text-[#9AA096]">Email:</span> {b.email}</p>
                  <p><span className="text-[#9AA096]">Telefone:</span> {b.telefone || "—"}</p>
                  <p><span className="text-[#9AA096]">Entidade:</span> {b.tipo_entidade}</p>
                  <p><span className="text-[#9AA096]">Consulta:</span> {b.tipo_consulta}</p>
                  <p><span className="text-[#9AA096]">Formato:</span> {b.formato}</p>
                  <p><span className="text-[#9AA096]">Preferência:</span> {b.data_preferencial ? new Date(b.data_preferencial).toLocaleString("pt-PT") : "A combinar"}</p>
                </div>
                <p className="text-sm text-ebony mt-3"><span className="text-[#9AA096]">Assunto:</span> {b.assunto}</p>
                {b.mensagem && <p className="text-sm text-[#757A70] mt-2 leading-relaxed border-l-2 border-[#E2E4DC] pl-4">{b.mensagem}</p>}
                <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#9AA096] mt-4">
                  Recebido a {new Date(b.created_at).toLocaleString("pt-PT")}
                </p>
              </div>
            ))}
          </div>
        )}

        {tab === "seguranca" && (
          <form
            onSubmit={changePassword}
            data-testid="admin-password-form"
            className="bg-white border border-[#E2E4DC] p-8 sm:p-10 max-w-lg"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold mb-3">
              Alterar Palavra-passe
            </p>
            <p className="text-xs text-[#757A70] leading-relaxed mb-8">
              Escolha uma palavra-passe sua (mínimo 8 caracteres). A sessão atual mantém-se ativa.
            </p>
            <label className="overline-label block mb-2 !text-[10px]">Palavra-passe atual</label>
            <input
              data-testid="admin-current-password-input"
              type="password"
              required
              value={pw.current}
              onChange={(e) => setPw({ ...pw, current: e.target.value })}
              className="input-field mb-5"
              autoComplete="current-password"
            />
            <label className="overline-label block mb-2 !text-[10px]">Nova palavra-passe</label>
            <input
              data-testid="admin-new-password-input"
              type="password"
              required
              minLength={8}
              value={pw.next}
              onChange={(e) => setPw({ ...pw, next: e.target.value })}
              className="input-field mb-5"
              autoComplete="new-password"
            />
            <label className="overline-label block mb-2 !text-[10px]">Confirmar nova palavra-passe</label>
            <input
              data-testid="admin-confirm-password-input"
              type="password"
              required
              value={pw.confirm}
              onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
              className="input-field mb-8"
              autoComplete="new-password"
            />
            <button
              type="submit"
              data-testid="admin-password-submit-button"
              disabled={pwSaving}
              className="btn-primary disabled:opacity-60"
            >
              {pwSaving ? "A guardar…" : "Guardar Nova Palavra-passe"}
            </button>
          </form>
        )}
      </div>
    </main>
  );
}
